import { maybeQueueMicrotask } from './maybeQueueMicrotask';
import { withResolvers } from './withResolvers';

// Core function types

// Internal data structures

// Batch configuration types

// Scheduler interface

const createBatchScheduler = configInput => {
  // Clone & freeze config to prevent bugs from mutation
  const config = Object.freeze(Object.assign({}, configInput));

  // Track all current batches, tracking calls that will be fulfilled with a single request with their timing controls
  const batches = new Map();

  // Track all pending calls (those whose underlying request hasn't yet been dispatched)
  const pendingCalls = new Map();

  // Track all dispatched calls (those whose request has been dispatched and are awaiting a response)
  const dispatchedCalls = new Map();

  // Execute a batch of calls for a given partition
  const executeBatch = async partition => {
    const batchData = batches.get(partition);
    batches.delete(partition);
    clearTimeout(batchData === null || batchData === void 0 ? void 0 : batchData.timeoutId);

    // If the batch is empty, we can just return
    if (!batchData || batchData.calls.size === 0) {
      return;
    }

    // Get call data from pendingCalls, copy into dispatched
    const callsData = [];
    for (const identityKey of batchData.calls) {
      const callData = pendingCalls.get(identityKey);
      if (callData) {
        callsData.push(callData);
        dispatchedCalls.set(identityKey, callData);
        pendingCalls.delete(identityKey);
      }
    }
    try {
      // Execute this batch
      const response = await config.execute(callsData.reduce((acc, {
        identityKey,
        input
      }) => {
        acc[identityKey] = input;
        return acc;
      }, {}), partition);
      for (const {
        identityKey,
        input,
        resolve,
        reject
      } of callsData) {
        try {
          resolve(config.extract({
            response,
            input,
            identity: identityKey
          }));
        } catch (error) {
          // Reject individual calls if extraction rejects
          reject(error);
        }
        dispatchedCalls.delete(identityKey);
      }
    } catch (error) {
      // Reject entire batch if request fails
      for (const {
        identityKey,
        reject
      } of callsData) {
        reject(error);
        dispatchedCalls.delete(identityKey);
      }
    }
  };
  const queueUnsafe = input => {
    var _pendingCalls$get, _batches$get;
    const identityKey = config.identity(input);
    const partition = config.partition(input);

    // Check for duplicates
    if (config.mode === 'read') {
      const existingCallData = pendingCalls.get(identityKey) || dispatchedCalls.get(identityKey);
      if (existingCallData) {
        return existingCallData.promise;
      }
    } else if (config.mode === 'write') {
      // Only dedupe pending calls in write mode
      const existingCallData = pendingCalls.get(identityKey);
      if (existingCallData) {
        const oldPartition = existingCallData.partition;

        // Update the existing call's input and partition (last-write-wins)
        existingCallData.input = input;
        existingCallData.partition = partition;

        // If same partition, we're done and can return the existing promise
        if (oldPartition === partition) {
          return existingCallData.promise;
        }

        // Otherwise, we're switching partitions
        const oldBatchData = batches.get(oldPartition);
        if (oldBatchData) {
          // Remove the call from the old partition
          oldBatchData.calls.delete(identityKey);

          // If the old partition is now empty, clean it up
          if (oldBatchData.calls.size === 0) {
            batches.delete(oldPartition);
            if (oldBatchData.timeoutId) {
              clearTimeout(oldBatchData.timeoutId);
            }
          }
        }

        // Fall through to add to new partition
      }
    }

    // If we get here with a value in pendingCalls, we need to reuse it (only relevant when switching batches in write mode).
    // Otherwise, we need to create a new promise.
    const {
      promise,
      resolve,
      reject
    } = (_pendingCalls$get = pendingCalls.get(identityKey)) !== null && _pendingCalls$get !== void 0 ? _pendingCalls$get : withResolvers();
    const callData = {
      identityKey,
      promise,
      input,
      partition,
      resolve,
      reject
    };
    pendingCalls.set(identityKey, callData);

    // Add identity to batch
    const batchData = (_batches$get = batches.get(partition)) !== null && _batches$get !== void 0 ? _batches$get : {
      calls: new Set()
    };
    batchData.calls.add(identityKey);
    batches.set(partition, batchData);

    // Execute the batch immediately if we've reached or somehow surpassed the chunk size
    if (batchData.calls.size >= config.chunkSize) {
      void executeBatch(partition);
      return promise;
    }
    if (config.type === 'debounce') {
      // If debouncing, clear and re-set the timeout
      if (batchData.timeoutId) {
        clearTimeout(batchData.timeoutId);
      }
      batchData.timeoutId = setTimeout(() => executeBatch(partition), config.timeMs);
    } else if (config.type === 'window') {
      // If windowing, set the timeout if it doesn't already exist
      if (!batchData.timeoutId) {
        batchData.timeoutId = setTimeout(() => executeBatch(partition), config.timeMs);
      }
    } else if (config.type === 'immediate') {
      // If immediate, queue a microtask to execute the batch
      if (!batchData.microtaskScheduled) {
        batchData.microtaskScheduled = true;
        maybeQueueMicrotask(() => void executeBatch(partition));
      }
    }
    return promise;
  };

  /**
   * Queue a call in the batch scheduler.
   *
   * Returns a promise that will resolve to the result of the call.
   */
  const queue = input => {
    try {
      return queueUnsafe(input);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  /**
   * Abort all pending calls. Primarily intended for cleaning up between test specs.
   *
   * Note: In-flight batches may still complete and could affect deduping
   * of identical calls dispatched after abort.
   */
  const abort = () => {
    // Clear all timeouts
    for (const batchData of batches.values()) {
      if (batchData.timeoutId) {
        clearTimeout(batchData.timeoutId);
      }
    }

    // Reject all external promises
    for (const callData of [...pendingCalls.values(), ...dispatchedCalls.values()]) {
      callData.reject(Error('Cancelled'));
    }

    // Clear all maps
    batches.clear();
    pendingCalls.clear();
    dispatchedCalls.clear();
  };

  /**
   * Flush all pending batches. This is a cleaner version of abort that immediately dispatches any pending calls
   * then waits on all dispatched calls to settle.
   *
   * Note: rejections will be swallowed here (i.e flush always resolves), but are still surfaced to the queuer.
   */
  const flush = async () => {
    // Execute all pending batches immediately
    // This synchronously moves all pending calls into the dispatchedCalls map
    for (const partitionKey of batches.keys()) {
      void executeBatch(partitionKey);
    }
    const allPromises = [...dispatchedCalls.values()].map(callData => callData.promise);

    // Wait for all dispatched calls to complete
    await Promise.allSettled(allPromises);
  };
  return {
    queue,
    abort,
    flush
  };
};

/**
 * A fluent builder that creates a batch scheduler.
 *
 * The scheduler provides an interface to queue "calls" that can be partitioned
 * into batches, which are then dispatched with various timing strategies.
 *
 * @example
 * ```typescript
 * // Read mode - optimized for data fetching with deduplication
 * const userReader = scheduler
 *   .read<{ userId: number }>()
 *   .identity(({ userId }) => userId.toString())
 *   .partition(() => 'users')
 *   .execute(fetchUsersBatch)
 *   .extract((batchResponse, { userId }) => batchResponse.users[userId])
 *   .debounce(100);
 *
 *
 * const user1 = await userReader.queue({ userId: 123 });
 *
 * // Write mode - optimized for data updates with last-write-wins
 * const userWriter = scheduler
 *   .write<{ userId: number, name: string }>()
 *   .identity(({ userId }) => userId.toString())
 *   .partition(() => 'users')
 *   .execute(saveUsersBatch)
 *   .extract((batchResponse, { userId }) => batchResponse.users[userId])
 *   .debounce(500);
 *
 * // Both will resolve at the same time to the result of write2
 * const write1 = userWriter.queue({ userId: 123, name: 'Alice' });
 * const write2 = userWriter.queue({ userId: 123, name: 'Bob' });
 *
 * write1 === write2; // true — both variables reference the same promise
 *
 * ```
 */
export const batchScheduler = () => {
  const createBuilder = mode => ({
    /**
     * Specify the identity function that will be used to deduplicate calls. Will be passed a call input
     * and is expected to return a string uniquely identifying that call.
     *
     * Note: This identity key must be globally unique — it is **not** namespaced by partition.
     */
    identity: identityFn => ({
      /**
       * Specify the partition function that will be used to group calls into a batch.
       *
       * A "batch" is a set of calls that will be fulfilled by a single underlying promise,
       * e.g a group of objects that can be fulfilled by a single http request.
       */
      partition: partitionFn => ({
        /**
         * Specify the execute function that fulfills a batch. Will be provided
         * a map of call identity > input and is expected to turn those inputs into a single promise.
         */
        execute: executeFn => ({
          /**
           * Specify the extraction function that is provided a single call input + identity
           * and is expected to extract the result for that call from the result
           * of the execute function.
           */
          extract: extractFn => {
            const timingChain = chunkSize => {
              const common = {
                identity: identityFn,
                partition: partitionFn,
                execute: executeFn,
                extract: extractFn,
                chunkSize,
                mode
              };
              return {
                /**
                 * Create an immediate mode scheduler which batches calls only within this tick of the event loop (zero-tick batching).
                 */
                immediate: () => createBatchScheduler(Object.assign({}, common, {
                  type: 'immediate'
                })),
                /**
                 * Create a debouncing scheduler which waits until calls to a partition have been quiet for `debounceMs` millis before
                 * dispatching a batch.
                 */
                debounce: debounceMs => {
                  if (debounceMs < 0) {
                    throw Error('Invalid debounceMs: Must be greater than or equal to zero');
                  }
                  return createBatchScheduler(Object.assign({}, common, {
                    type: 'debounce',
                    timeMs: debounceMs
                  }));
                },
                /**
                 * Create a windowed scheduler collects calls for `windowMs` millis then dispatches.
                 */
                window: windowMs => {
                  if (windowMs < 0) {
                    throw Error('Invalid windowMs: Must be greater than or equal to zero');
                  }
                  return createBatchScheduler(Object.assign({}, common, {
                    type: 'window',
                    timeMs: windowMs
                  }));
                }
              };
            };
            return Object.assign({
              /**
               * Set the chunk size - batches will be dispatched immediately when this many calls are queued.
               *
               * Must be a positive integer.
               */
              chunkSize: size => {
                if (!Number.isInteger(size) || size <= 0) {
                  throw Error('Invalid chunkSize: Must be a positive integer');
                }
                return timingChain(size);
              }
            }, timingChain(Number.POSITIVE_INFINITY));
          }
        })
      })
    })
  });
  return {
    /**
     * Create a read-optimized scheduler with first-write-wins deduplication that will
     * dedupe against dispatched calls.
     *
     * Ideal for data fetching where you want to minimize redundant requests.
     */
    read: () => createBuilder('read'),
    /**
     * Create a write-optimized scheduler with last-write-wins deduplication that will
     * not dedupe against dispatched calls.
     *
     * Ideal for data updates where the latest user input should take precedence, but once dispatched,
     * the scheduler will create a new batch for the same identity.
     */
    write: () => createBuilder('write')
  };
};